import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NewProductOrServiceTableRow, ProductOrServiceType, TableState } from 'types';
import { getProductsServices } from 'services/product-service/getProductsServices';
import { type ProductServiceOutput } from 'pco-product-service-internal-consumer';
import { setHasMemberCode } from 'reducers/partnerStatusReducer';
import { closeModal, openModal } from 'reducers/modalReducer';

export const initialTableState: TableState = {
  statusFilter: [],
  typeFilter: [],
  selectedProduct: undefined,
  tableItems: [],
  isLoading: false,
};

export const fetchProductsServicesThunk = createAsyncThunk(
  'TABLE/fetchProductsServicesTable',
  async ({ partnerId, navigate }: { partnerId: string; navigate: (path: string) => void }, thunkAPI) => {
    try {
      const data = await getProductsServices(partnerId);

      const items: NewProductOrServiceTableRow[] = data.productsServicesOutput?.map((item: ProductServiceOutput) => {
        return { ...item, account: item.accountInformation?.account ?? '-' }; // TODO - add to html not here
      }) as unknown as NewProductOrServiceTableRow[];
      const filterOptionsStatusArray = Array.from(new Set(items.map((item) => item.agreementStatus)));
      thunkAPI.dispatch(setStatusTypeFilter(filterOptionsStatusArray));
      // check if data contains member code
      if (items.filter((item) => item.view === ProductOrServiceType.MEMBERZONE).length > 0) {
        thunkAPI.dispatch(setHasMemberCode(true));
      }
      return data;
    } catch (err: unknown) {
      thunkAPI.dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: err,
            okHandler: () => {
              navigate(`/partner?partnerNumber=${partnerId}`);
              thunkAPI.dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when retrieving products',
              userErrorMessage: `An error occurred when retrieving products. Please try again later.`,
            },
          },
        }),
      );
    }
  },
);

const tableSlice = createSlice({
  name: 'TABLE',
  initialState: initialTableState,
  reducers: {
    setStatusTypeFilter: (state, action: PayloadAction<string[]>) => {
      return { ...state, statusFilter: action.payload };
    },
    setTypeFilter: (state, action: PayloadAction<string[]>) => {
      return { ...state, typeFilter: action.payload };
    },
    setSelectedProduct: (state, action: PayloadAction<string | undefined>) => {
      return { ...state, selectedProduct: action.payload };
    },
    setTableItems: (state, action: PayloadAction<NewProductOrServiceTableRow[]>) => {
      return {
        ...state,
        tableItems: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProductsServicesThunk.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(fetchProductsServicesThunk.fulfilled, (state, action) => {
      return {
        ...state,
        tableItems:
          typeof action.payload === 'string'
            ? []
            : (action.payload?.productsServicesOutput as unknown as NewProductOrServiceTableRow[]),
        isLoading: false,
      };
    });
    builder.addCase(fetchProductsServicesThunk.rejected, (state) => {
      return { ...state, isLoading: false };
    });
  },
});

export const { setStatusTypeFilter, setTypeFilter, setSelectedProduct, setTableItems } = tableSlice.actions;
export default tableSlice.reducer;
